import { saveBookingAttemptThunk } from 'actions/bookingAttempts/BookingAttemptsActions';
import {
    ONLINE_BOOKING_NAVIGATE_TO_STEP,
    ONLINE_BOOKING_SET_NOTES,
    OnlineBookingAction
} from 'actions/onlineBooking/OnlineBookingActionsTypes';
import { Dispatch, Middleware, MiddlewareAPI } from 'redux';
import { RootState } from 'store';
import { RootAction } from 'types/Actions';
import {
    isOnlineBookingBoardingItem,
    isOnlineBookingDaycareItem,
    isOnlineBookingGroomingItem,
    isOnlineBookingSessionItem,
    OnlineBookingStep
} from 'types/OnlineBooking';
import { getOnlineBookingAddonUnits } from 'utils/onlineBookingAddons';

const SKIP_STEPS = new Set([
    OnlineBookingStep.Home,
    OnlineBookingStep.Signin,
    OnlineBookingStep.Signup,
    OnlineBookingStep.Success,
    OnlineBookingStep.JoinWaitlist,
    OnlineBookingStep.WaitlistSuccess,
    // Packages & Memberships demo, nothing to track as a booking
    OnlineBookingStep.Purchase,
    OnlineBookingStep.PurchaseCheckout,
    OnlineBookingStep.PurchaseSuccess
]);

const SAVED_ACTIONS = new Set<string>([ONLINE_BOOKING_NAVIGATE_TO_STEP, ONLINE_BOOKING_SET_NOTES]);

export const bookingAttemptMiddleware: Middleware =
    ({ getState, dispatch: storeDispatch }: MiddlewareAPI<Dispatch<RootAction>, RootState>) =>
    (dispatch: Dispatch<RootAction>) =>
    (action: OnlineBookingAction) => {
        if (!SAVED_ACTIONS.has(action.type)) {
            return dispatch(action);
        }

        // Apply the action first so getState() reflects its effect
        // (notes update is in the reducer; step nav is too).
        const result = dispatch(action);

        const {
            marketplace: { data: marketplace },
            bookingAttempts,
            onlineBooking
        } = getState();

        const status: string =
            action.type === ONLINE_BOOKING_NAVIGATE_TO_STEP
                ? action.payload.step
                : onlineBooking.currentStep;

        if (SKIP_STEPS.has(status as OnlineBookingStep)) {
            return result;
        }

        if (!onlineBooking.customer.id) {
            return result;
        }

        const items = onlineBooking.bookingItems.filter(item => item.pet.id);

        const bookingData: Record<string, unknown> = {
            notes: onlineBooking.notes,
            bookingItems: items.map(item => {
                const base = {
                    petId: item.pet.id,
                    serviceId: item.service?.id,
                    addons: item.addons.map(a => ({
                        id: a.id,
                        quantity: getOnlineBookingAddonUnits(item, a)
                    })),
                    serviceType: item.businessArea
                };

                if (isOnlineBookingBoardingItem(item)) {
                    return {
                        ...base,
                        startDate: item.startDate?.format('YYYY-MM-DD'),
                        endDate: item.endDate?.format('YYYY-MM-DD'),
                        roomUnitId: item.roomUnitId,
                        feedings: item.feedings,
                        medications: item.medications
                    };
                }

                if (isOnlineBookingDaycareItem(item)) {
                    return {
                        ...base,
                        dates: item.days?.map(day => day.date.format('YYYY-MM-DD')),
                        dropOffTime: item.dropOffTime?.label,
                        pickUpTime: item.pickUpTime?.label,
                        roomUnitIds: item.days?.map(day => day.roomUnitId),
                        feedings: item.feedings,
                        medications: item.medications
                    };
                }

                if (isOnlineBookingGroomingItem(item)) {
                    return {
                        ...base,
                        staffId: item.staff?.id ?? item.time?.staffId,
                        date: item.date?.format('YYYY-MM-DD'),
                        time: item.time?.label
                    };
                }

                if (isOnlineBookingSessionItem(item)) {
                    return {
                        ...base,
                        staffId: item.staff?.id ?? item.time?.staffId,
                        preferredStaffId: item.preferredStaff?.id,
                        date: item.date?.format('YYYY-MM-DD'),
                        time: item.time?.label,
                        addressId: item.addressId
                    };
                }

                return base;
            })
        };

        saveBookingAttemptThunk({
            sessionId: bookingAttempts.bookingAttempt?.sessionId,
            marketplaceId: marketplace.id,
            status,
            customerId: onlineBooking.customer.id,
            lead: onlineBooking.customer.lead,
            closed: false,
            notified: false,
            bookingData
        })(storeDispatch, getState, null);

        return result;
    };
